"use client";

import React, { useState } from "react";
import { ArrowRight, Check } from "lucide-react";

export interface MemorylayerEpilogueBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    placeholder?: string;
    buttonText?: string;
}

export default function MemorylayerEpilogueBlock({
    tag = "Get in Touch",
    title = "Instantiate Your Own Agentic Blueprint",
    description = "Ready to design custom vector layers, reduce LLM context costs, and launch conversational agents? Let's write the specifications.",
    placeholder = "Enter your email...",
    buttonText = "Initiate Connection"
}: MemorylayerEpilogueBlockProps) {
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSubmitted(true);
        // Reset email
        setEmail("");
    };

    return (
        <section className="py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0C] w-full" data-header-theme="light">
            <div className="container mx-auto max-w-4xl text-center space-y-8">
                {tag && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 w-fit mx-auto">
                        <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                        <span className="text-[10px] font-mono text-purple-300 uppercase tracking-widest font-semibold">{tag}</span>
                    </div>
                )}
                
                <h2 className="text-4xl md:text-6xl font-light text-white leading-tight font-sans tracking-tight">
                    {title === "Instantiate Your Own Agentic Blueprint" ? (
                        <>
                            Instantiate Your Own <br />
                            <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">Agentic Blueprint</span>
                        </>
                    ) : (
                        title
                    )}
                </h2>

                {description && (
                    <p className="text-zinc-400 text-sm max-w-md mx-auto leading-relaxed font-light font-sans">
                        {description}
                    </p>
                )}

                {/* Cyberpunk Terminal Input Form */}
                <div className="max-w-xl mx-auto pt-6">
                    {submitted ? (
                        <div className="bg-[#121214] border border-emerald-500/30 rounded-xl p-6 flex flex-col items-center space-y-3 shadow-[0_10px_30px_rgba(16,185,129,0.05)]">
                            <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                                <Check className="w-5 h-5 text-emerald-400" />
                            </div>
                            <span className="text-xs font-mono text-emerald-400 uppercase tracking-wider">Connection Protocol Initiated</span>
                            <p className="text-[11px] font-mono text-zinc-500">
                                SYSTEM: We will trace back to your destination node shortly. Check your inbox registry.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="bg-[#121214] border border-zinc-850 rounded-xl p-2.5 flex flex-col md:flex-row gap-2.5 shadow-xl group focus-within:border-purple-500/40 transition-colors">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={placeholder}
                                required
                                className="flex-1 bg-transparent border-none outline-none text-xs text-zinc-300 font-mono px-4 py-3 focus:ring-0 focus:outline-none"
                                suppressHydrationWarning={true}
                            />
                            <button type="submit" className="px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white font-mono text-xs uppercase tracking-widest font-semibold rounded-lg transition-colors flex items-center justify-center gap-2">
                                {buttonText} <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
